import { useId } from 'react';
import { ChartBar } from '@phosphor-icons/react';
import { ScreenReaderLabel } from '../../../components/common/ScreenReaderLabel';
import { SimpleTooltip } from '../../../components/ui/tooltip';

interface CreatorAnalysisButtonProps {
  /** Opens the creator's analysis drawer. */
  onOpen: () => void;
  /** Creator display name — used for the accessible label only. */
  creatorName?: string;
}

function analysisLabel(creatorName: string | undefined): string {
  if (!creatorName) return 'View creator analysis';
  return `View ${creatorName}'s analysis`;
}

/**
 * Dedicated, always-visible affordance that opens a creator's analysis drawer.
 * Sits on the right edge of the Creator column in the Creators + Finalized
 * tabs. Soft-chip treatment in the brand ramp — this is the one place the
 * purple accent is earned, because the drawer surfaces AI agent output
 * (Audience Intelligence + Brand Safety).
 */

export function CreatorAnalysisButton({ onOpen, creatorName }: CreatorAnalysisButtonProps) {
  const labelId = useId();
  return (
    <SimpleTooltip content="View analysis">
      <button
        type="button"
        onClick={(e) => {
          // Guard against any row-level click handler double-firing.
          e.stopPropagation();
          onOpen();
        }}
        // Unconditional — a no-op outside PublicCampaignShell's click-capture
        // interceptor (see that file's `isAllowedClick` doc comment); inside
        // it, this is what lets the click reach `onOpen` at all instead of
        // being swallowed before it fires.
        data-public-allowed=""
        aria-labelledby={labelId}
        className="flex size-8 shrink-0 items-center justify-center rounded-full bg-(--brand-50) text-(--brand-500) transition-colors hover:bg-(--brand-100) focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--brand-500)/40"
      >
        <ChartBar size={16} weight="duotone" />
        <ScreenReaderLabel id={labelId}>{analysisLabel(creatorName)}</ScreenReaderLabel>
      </button>
    </SimpleTooltip>
  );
}
